import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Skeleton } from './ui/skeleton';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import InventoryEditor from './InventoryEditor';

const fmt = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const rangeFor = (preset, custom) => {
  const now = new Date();
  if (preset === 'all') return {};
  if (preset === 'today') { const t = fmt(now); return { date_from: t, date_to: t }; }
  if (preset === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); const t = fmt(y); return { date_from: t, date_to: t }; }
  if (preset === 'week') { const s = new Date(now); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); return { date_from: fmt(s), date_to: fmt(now) }; }
  if (preset === 'month') return { date_from: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), date_to: fmt(now) };
  if (preset === 'lastmonth') {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0);
    return { date_from: fmt(s), date_to: fmt(e) };
  }
  if (preset === 'custom' && custom.from) return { date_from: custom.from, date_to: custom.to || custom.from };
  return {};
};
const inr = (v) => (v >= 10000000 ? `₹${(v / 10000000).toFixed(2)} Cr` : v >= 100000 ? `₹${(v / 100000).toFixed(1)} L` : `₹${(v || 0).toLocaleString('en-IN')}`);

const Section = ({ icon, title, children, testid }) => {
  const Icon = Icons[icon] || Icons.LayoutGrid;
  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={testid}>
      <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        <Icon className="w-3.5 h-3.5 text-brand-gold" /> {title}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
};

const PropertyDetailsDrawer = ({ propertyId, open, onOpenChange }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preset, setPreset] = useState('all');
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = useCallback(async () => {
    if (!propertyId) return;
    try {
      const params = rangeFor(preset, custom);
      const r = await api.get(`/properties/${propertyId}/details`, { params });
      setData(r.data);
      setUpdatedAt(new Date());
    } catch (e) { console.error('Project details load failed', e); }
    finally { setLoading(false); }
  }, [propertyId, preset, custom]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [open, load]);

  const project = data?.project;
  const goLeads = (extra, label) => {
    onOpenChange(false);
    navigate('/leads', { state: { filterProject: project?.name, ...extra, filterLabel: `${project?.name} — ${label}` } });
  };
  const Stat = ({ label, value, onClick, tone = 'text-slate-800', testid }) => (
    <button type="button" onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`text-left rounded-md border border-slate-200 px-2.5 py-2 w-full ${onClick ? 'hover:border-brand-gold hover:bg-amber-50/40 cursor-pointer' : ''}`}>
      <div className={`font-display text-lg font-bold ${tone}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </button>
  );

  const lv = data?.leads;
  const inv = data?.inventory;
  const bk = data?.bookings;
  const funnelMax = useMemo(() => Math.max(1, ...(lv?.funnel || []).map(f => f.count)), [lv]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="project-details-drawer">
        {loading || !data ? (
          <div className="p-5 space-y-3">
            <SheetHeader className="sr-only"><SheetTitle>Project Details</SheetTitle></SheetHeader>
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
          </div>
        ) : (
          <>
            <SheetHeader className="p-4 pb-3 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
              <SheetTitle className="font-display text-lg flex items-center gap-2">
                <Icons.Building2 className="w-5 h-5 text-brand-gold" /> {project?.name}
              </SheetTitle>
              <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1"><Icons.MapPin className="w-3 h-3" />{project?.location}</span>
                <Badge variant="outline" className="text-[10px]">{project?.status}</Badge>
                <Badge variant="outline" className="text-[10px]">{project?.type}</Badge>
                {project?.coordinator && <Badge className="bg-brand-navy text-white text-[10px]">Coordinator: {project.coordinator}</Badge>}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Select value={preset} onValueChange={setPreset}>
                  <SelectTrigger className="h-8 w-[150px] text-[11px] font-bold uppercase" data-testid="project-date-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[['all', 'All Time'], ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'This Week'], ['month', 'This Month'], ['lastmonth', 'Last Month'], ['custom', 'Custom Range']].map(([v, l]) =>
                      <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                {preset === 'custom' && (
                  <>
                    <Input type="date" className="h-8 w-[130px] text-xs" value={custom.from} onChange={e => setCustom(c => ({ ...c, from: e.target.value }))} />
                    <Input type="date" className="h-8 w-[130px] text-xs" value={custom.to} onChange={e => setCustom(c => ({ ...c, to: e.target.value }))} />
                  </>
                )}
                <span className="text-[10px] text-slate-400 ml-auto" data-testid="project-last-updated">
                  Last Updated: {updatedAt?.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load} data-testid="project-refresh-btn"><Icons.RefreshCw className="w-3.5 h-3.5" /></Button>
              </div>
            </SheetHeader>

            <div className="p-4 space-y-3">
              {/* 2. Inventory Status */}
              <Section icon="Warehouse" title="Inventory Status" testid="section-inventory">
                <div className="grid grid-cols-4 gap-2">
                  <Stat label="Total" value={inv.total_inventory} />
                  <Stat label={`Available ${inv.available_pct}%`} value={inv.available_units} tone="text-emerald-600" />
                  <Stat label={`Booked ${inv.booked_pct}%`} value={inv.booked_units} tone="text-brand-navy" onClick={() => { onOpenChange(false); navigate('/bookings'); }} testid="stat-booked-units" />
                  <Stat label={`Locked ${inv.locked_pct}%`} value={inv.locked_units} tone="text-amber-600" />
                </div>
                <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden flex">
                  <div className="bg-brand-navy" style={{ width: `${inv.booked_pct}%` }} />
                  <div className="bg-amber-400" style={{ width: `${inv.locked_pct}%` }} />
                  <div className="bg-emerald-400" style={{ width: `${inv.available_pct}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 mt-1">Occupancy: {inv.occupancy_pct}%</div>
                {inv.by_type.length > 0 && (
                  <table className="w-full text-xs mt-2">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th>Property Type</th><th>Total</th><th>Avail</th><th>Booked</th><th>Locked</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {inv.by_type.map(t => (
                        <tr key={t.type}><td className="py-1 font-medium">{t.type}</td><td>{t.total}</td><td className="text-emerald-600">{t.available}</td><td className="text-brand-navy">{t.booked}</td><td className="text-amber-600">{t.locked}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* 3-4. Lead Summary + Status Report */}
              <Section icon="Users" title={`Project Leads — Total ${lv.total}`} testid="section-lead-status">
                {lv.total === 0 ? <div className="text-xs text-slate-400 text-center py-3">0 leads in this range — no data yet.</div> : (
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th>Status</th><th>Count</th><th>%</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {lv.by_status.map(s => (
                        <tr key={s.status} className="hover:bg-amber-50/50 cursor-pointer" onClick={() => goLeads({ filterStatus: s.status }, s.status)} data-testid={`status-row-${s.status.replace(/\s+/g, '-')}`}>
                          <td className="py-1.5 font-medium">{s.status}</td><td>{s.count}</td><td className="text-slate-500">{s.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* 5-6. Source + Priority */}
              <div className="grid sm:grid-cols-2 gap-3">
                <Section icon="Megaphone" title="Lead Source Report" testid="section-lead-source">
                  {lv.by_source.length === 0 ? <div className="text-xs text-slate-400">0 — no leads</div> :
                    lv.by_source.map(s => (
                      <button key={s.source} className="w-full flex justify-between text-xs py-1 border-b border-slate-50 hover:text-brand-navy" onClick={() => goLeads({ filterSource: s.source }, s.source)}>
                        <span>{s.source}</span><span className="font-bold">{s.count}</span>
                      </button>
                    ))}
                </Section>
                <Section icon="Flame" title="Lead Priority Report" testid="section-lead-priority">
                  {lv.by_priority.length === 0 ? <div className="text-xs text-slate-400">0 — no leads</div> :
                    lv.by_priority.map(p => (
                      <button key={p.priority} className="w-full flex justify-between text-xs py-1 border-b border-slate-50 hover:text-brand-navy" onClick={() => goLeads({ filterPriority: p.priority }, `${p.priority} priority`)} data-testid={`priority-row-${p.priority}`}>
                        <span>{p.priority}</span><span className="font-bold">{p.count}</span>
                      </button>
                    ))}
                </Section>
              </div>

              {/* 7. Funnel */}
              <Section icon="Filter" title="Lead Funnel" testid="section-funnel">
                {lv.funnel.map(f => (
                  <button key={f.stage} className="w-full flex items-center gap-2 py-0.5 group" onClick={() => goLeads({ filterStatus: f.stage }, f.stage)}>
                    <span className="text-[10px] w-40 text-left text-slate-600 truncate group-hover:text-brand-navy">{f.stage}</span>
                    <div className="flex-1 h-3.5 bg-slate-100 rounded overflow-hidden">
                      <div className="h-full bg-brand-navy/80 rounded" style={{ width: `${(f.count / funnelMax) * 100}%` }} />
                    </div>
                    <span className="text-xs font-bold w-6 text-right">{f.count}</span>
                  </button>
                ))}
              </Section>

              {/* 8-9. Booking summary + report */}
              <Section icon="KeyRound" title="Project Booking Summary" testid="section-bookings">
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Booked Units" value={bk.booked_units} onClick={() => { onOpenChange(false); navigate('/bookings'); }} />
                  <Stat label="Vacant Units" value={bk.vacant_units} tone="text-emerald-600" />
                  <Stat label="Locked Units" value={bk.locked_units} tone="text-amber-600" />
                  <Stat label="Bookings" value={bk.count} />
                  <Stat label="Booked Value" value={inr(bk.total_value)} tone="text-brand-navy" />
                  <Stat label="Avg Value" value={inr(bk.avg_value)} />
                </div>
                {bk.report.length > 0 && (
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-[11px]">
                      <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th>Date</th><th>Customer</th><th>Unit</th><th>Agent</th><th>Token</th><th>Value</th><th>Payment</th></tr></thead>
                      <tbody className="divide-y divide-slate-100">
                        {bk.report.map(r => (
                          <tr key={r.id}><td className="py-1">{r.booking_date}</td><td>{r.customer_name}</td><td>{r.unit_no}</td><td>{r.agent}</td><td>{inr(r.booking_amount)}</td><td>{inr(r.total_value)}</td>
                            <td><Badge variant="outline" className={`text-[9px] ${r.payment_status === 'Fully Paid' ? 'text-emerald-700 border-emerald-300' : r.payment_status === 'Partial' ? 'text-amber-700 border-amber-300' : 'text-slate-500'}`}>{r.payment_status}</Badge></td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Section>

              {/* 10. Team performance */}
              <Section icon="Trophy" title="Sales Team Performance" testid="section-team">
                {data.team.length === 0 ? <div className="text-xs text-slate-400 text-center py-2">No leads assigned yet.</div> : (
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th>Agent</th><th>Leads</th><th>Hot</th><th>Visits</th><th>Done</th><th>Booked</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.team.map(t => (
                        <tr key={t.name} className="hover:bg-amber-50/50 cursor-pointer" onClick={() => goLeads({}, `${t.name}'s leads`)}>
                          <td className="py-1.5 font-medium">{t.name}</td><td>{t.leads}</td><td className="text-rose-600">{t.hot}</td><td>{t.visits}</td><td>{t.visits_done}</td><td className="text-emerald-700">{t.booked}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* 11. Customer summary */}
              <Section icon="Contact" title="Customer / Lead Summary" testid="section-customers">
                <div className="grid grid-cols-2 gap-2">
                  <Stat label="Total Leads" value={data.customers.total_leads} onClick={() => goLeads({}, 'All leads')} />
                  <Stat label="Total Contacts" value={data.customers.total_contacts} onClick={() => { onOpenChange(false); navigate('/contacts'); }} />
                </div>
              </Section>

              {/* 12-14. Call activity + recordings */}
              <Section icon="Phone" title="Call Activity (Project)" testid="section-calls">
                <div className="grid grid-cols-5 gap-2 mb-2">
                  <Stat label="Total" value={data.calls.total} />
                  <Stat label="Connected" value={data.calls.connected} tone="text-emerald-600" />
                  <Stat label="Missed" value={data.calls.missed} tone="text-rose-600" />
                  <Stat label="Duration" value={data.calls.total_duration} />
                  <Stat label="Avg" value={data.calls.avg_duration} />
                </div>
                {data.calls.by_user.length > 0 ? (
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400"><th>User</th><th>Total</th><th>Connected</th><th>Missed</th><th>Duration</th><th>Last Call</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.calls.by_user.map(u => (
                        <tr key={u.user}><td className="py-1.5 font-medium">{u.user}</td><td>{u.total}</td><td>{u.connected}</td><td>{u.missed}</td><td>{u.duration}</td><td className="text-slate-500">{u.last || '—'}</td></tr>
                      ))}
                    </tbody>
                  </table>
                ) : <div className="text-xs text-slate-400 text-center py-2">0 calls logged for this project's leads.</div>}
              </Section>

              <Section icon="Mic" title="Call Recordings" testid="section-recordings">
                {data.recordings.length === 0 ? (
                  <div className="text-xs text-slate-400 text-center py-2">Recording Not Available — connect Cloud Telephony or attach recordings on call activities.</div>
                ) : data.recordings.map((r, idx) => (
                  <div key={`${r.at}-${idx}`} className="flex items-center gap-2 py-1.5 border-b border-slate-50 text-xs">
                    <Icons.PlayCircle className="w-4 h-4 text-brand-navy shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{r.lead} · {r.phone}</div>
                      <div className="text-[10px] text-slate-500">{r.user} · {(r.at || '').slice(0, 16).replace('T', ' ')}</div>
                    </div>
                    <audio controls preload="none" src={r.recording_url} className="h-7 max-w-[180px]" />
                  </div>
                ))}
              </Section>

              {hasPermission('admin', 'edit') && (
                <InventoryEditor projectId={propertyId} onChanged={load} />
              )}

              {hasPermission('properties', 'edit') && (
                <div className="text-[10px] text-slate-400 text-center pb-2">
                  Use “Edit” on the project card to update project info
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default PropertyDetailsDrawer;
